import axios from "axios";

// Base URL for API calls loaded from environment variables
const API_BASE_URL = process.env.REACT_APP_BACKEND_API;
const BASE_URL = `${API_BASE_URL}/api/public-recruitment`;

// Create axios instance with default config
const api = axios.create({
  baseURL: BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      console.error("Authentication failed. Please login again.");
      // Optionally redirect to login
      // window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const fetchApprovedJobs = async () => {
  try {
    const response = await api.get("/all", {
      params: { jobCreated: 1 },
    });

    if (response.data.operation === "success") {
      // Map backend data structure to frontend job structure
      const jobs = response.data.data.map((job) => ({
        id: job.RequestID,
        title: job.PositionTitle,
        company: job.DepartmentName || "GMS",
        location: "Pakistan", // Default location
        type: job.JobType || job.EmploymentType || "",
        salary: job.BudgetRange || "Negotiable",
        experience: `${job.ExperienceYears || 0}+ years`,
        description: job.JobDescription || "",
        requirements: [], // Can be parsed from description if needed
        responsibilities: [], // Can be parsed from description if needed
        designation: job.Designation || job.PositionTitle,
        qualification: job.Qualification || "",
        vacancies: job.Vacancies || 1,
        // Keep original data for reference
        requestID: job.RequestID,
        requisitionNumber: job.RequisitionNumber,
        departmentID: job.DepartmentID,
      }));

      return {
        success: true,
        data: jobs,
      };
    } else {
      throw new Error(response.data.message || "Failed to fetch jobs");
    }
  } catch (error) {
    console.error("Error fetching approved jobs:", error);
    return {
      success: false,
      error:
        error.response?.data?.message ||
        error.message ||
        "Failed to fetch jobs",
    };
  }
};

/**
 * Fetch a specific job by ID
 */
export const fetchJobById = async (jobId) => {
  try {
    // Fetch all approved jobs and filter by ID
    const result = await fetchApprovedJobs();

    if (result.success) {
      const job = result.data.find((j) => parseInt(j.id) === parseInt(jobId));

      if (job) {
        return {
          success: true,
          data: job,
        };
      } else {
        throw new Error("Job not found");
      }
    } else {
      throw new Error(result.error);
    }
  } catch (error) {
    console.error("Error fetching job by ID:", error);
    return {
      success: false,
      error: error.message || "Failed to fetch job details",
    };
  }
};

/**
 * Submit job application
 */
export const submitApplication = async (jobId, formData) => {
  try {
    // Create FormData for multipart/form-data
    const submitData = new FormData();

    // Map frontend form fields to backend expected fields
    submitData.append("requestID", jobId);
    submitData.append("firstName", formData.FirstName);
    submitData.append("lastName", formData.LastName);
    submitData.append("email", formData.Email);
    submitData.append("phone", formData.Phone);
    submitData.append("experienceYears", formData.ExperienceYears);
    submitData.append("currentCompany", formData.CurrentCompany);
    submitData.append("currentDesignation", formData.CurrentDesignation);
    submitData.append("currentSalary", formData.CurrentSalary);
    submitData.append("expectedSalary", formData.ExpectedSalary);
    submitData.append("qualification", formData.Qualification);
    submitData.append("skills", formData.Skills);
    submitData.append("coverLetter", formData.CoverLetter);
    submitData.append("source", "Web Application");

    // Append resume file if present
    if (formData.resume) {
      submitData.append("resume", formData.resume);
    }

    // Use axios directly to bypass the auth interceptor
    const response = await axios.post(
      `${BASE_URL}/submit-application`,
      submitData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );

    if (response.data.operation === "success") {
      return {
        success: true,
        data: response.data.data,
        message: response.data.message,
      };
    } else {
      throw new Error(response.data.message || "Failed to submit application");
    }
  } catch (error) {
    console.error("Error submitting application:", error);
    return {
      success: false,
      error:
        error.response?.data?.message ||
        error.message ||
        "Failed to submit application",
    };
  }
};

/**
 * Parse Resume PDF - Extract data from resume
 */
export const parseResume = async (pdfFile, jobId) => {
  try {
    const formData = new FormData();
    formData.append("resume", pdfFile);
    if (jobId) {
      formData.append("jobId", jobId);
    }

    const response = await axios.post(`${BASE_URL}/parse-resume`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });

    if (response.data.operation === "success") {
      return {
        success: true,
        data: response.data.data,
      };
    } else {
      throw new Error(response.data.error || "Failed to parse resume");
    }
  } catch (error) {
    console.error("Error parsing resume:", error);
    return {
      success: false,
      error:
        error.response?.data?.message ||
        error.message ||
        "Failed to parse resume",
    };
  }
};

/**
 * Fetch all candidates/applicants
 */
export const fetchCandidates = async (params = {}) => {
  try {
    const response = await api.get("/candidates", { params });

    if (response.data.operation === "success") {
      // Backend returns data in data.candidates format
      const candidates = response.data.data.candidates || [];

      // Map to frontend expected format
      const mappedCandidates = candidates.map((candidate) => ({
        CandidateID: candidate.id || candidate.CandidateID,
        RequestID: candidate.RequestID,
        FirstName: candidate.FirstName,
        LastName: candidate.LastName,
        Email: candidate.Email,
        Phone: candidate.Phone,
        ExperienceYears: candidate.ExperienceYears,
        CurrentCompany: candidate.CurrentCompany,
        CurrentDesignation: candidate.CurrentDesignation,
        CurrentSalary: candidate.CurrentSalary,
        ExpectedSalary: candidate.ExpectedSalary,
        Qualification: candidate.Qualification,
        Skills: candidate.Skills,
        Source: candidate.Source,
        ApplicantStatus: candidate.Status || candidate.currentStage,
        AppliedOn: candidate.appliedDate || candidate.CreatedDate,
        PositionTitle: candidate.position,
        RequisitionNumber: candidate.requisitionId,
        DepartmentID: candidate.DepartmentID,
        ResumePath: candidate.ResumePath,
      }));

      return {
        success: true,
        applicants: mappedCandidates,
        totalCount: response.data.data.totalCount,
        totalPages: response.data.data.totalPages,
        currentPage: response.data.data.currentPage,
      };
    } else {
      throw new Error(response.data.message || "Failed to fetch candidates");
    }
  } catch (error) {
    console.error("Error fetching candidates:", error);
    return {
      success: false,
      error:
        error.response?.data?.message ||
        error.message ||
        "Failed to fetch candidates",
    };
  }
};

export const fetchApplicantResume = async (candidateId) => {
  try {
    // Fetch all candidates and find the specific one
    const result = await fetchCandidates();

    if (result.success) {
      const candidate = result.applicants.find(
        (c) => parseInt(c.CandidateID) === parseInt(candidateId)
      );

      if (candidate && candidate.ResumePath) {
        return {
          success: true,
          resumeUrl: `${API_BASE_URL}/${candidate.ResumePath}`,
        };
      } else {
        throw new Error("Candidate or resume not found");
      }
    } else {
      throw new Error(result.error);
    }
  } catch (error) {
    console.error("Error fetching candidate resume:", error);
    return {
      success: false,
      error: error.message || "Failed to fetch candidate resume",
    };
  }
};
